/**
 * ProductionTimelineCards — Live production timeline display for the Quote
 * Summary / Walkthrough modal. Two side-by-side cards (VEXOS Platform +
 * Studio Visualization) with weeks headline, deliverable list, and a
 * dashed "begins once we receive all required files" footer line.
 *
 * Wired into VexosClientCalculator.jsx between the payment-schedule tiles
 * and the recurring-monthly pill. Pure display — no math lives here.
 */
import React from "react";

export default function ProductionTimelineCards({ timeline, isAlacarte }) {
  if (!timeline) return null;
  const {
    vexosWeeksDisplay,
    vexosBusinessDays,
    vizWeeksDisplay,
    vizBusinessDays,
  } = timeline;

  return (
    <section className="mt-10" data-testid="production-timeline-section" data-vexos-section="production-timeline">
      <div className="mb-4 flex items-baseline justify-between">
        <p className="text-[10.5px] uppercase tracking-[0.24em] text-[#8FA79B]/80 font-medium">Production Timeline</p>
        <p className="text-[11px] text-white/40 italic">Estimated delivery, starting once we receive all required files</p>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${isAlacarte ? "" : "lg:grid-cols-2"}`}>
        {/* ── Phase 1 · VEXOS Platform (hidden for A La Carte) ─────── */}
        {!isAlacarte && (
          <div
            className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-5"
            data-testid="ptl-card-vexos"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8FA79B]/65 font-medium mb-1">Phase 1</p>
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-[#8FA79B] font-semibold">VEXOS PLATFORM</p>
            <p className="text-[11px] text-white/45 mt-1 mb-6">Platform setup &amp; experience</p>

            <p
              className="text-[34px] leading-none text-white tabular-nums"
              style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
              data-testid="ptl-vexos-weeks"
            >
              {vexosWeeksDisplay}
            </p>
            <p className="text-[12px] text-white/45 mt-2 tabular-nums" data-testid="ptl-vexos-days">
              {vexosBusinessDays} business days
            </p>

            <p className="text-[10.5px] text-white/40 italic mt-6 pt-4 border-t border-dashed border-white/10">
              Begins once we receive all required files
            </p>
          </div>
        )}

        {/* ── Studio Visualization (Phase 2 with VEXOS; Phase 1 A La Carte) */}
        <div
          className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-5"
          data-testid="ptl-card-viz"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#8FA79B]/65 font-medium mb-1">{isAlacarte ? "Phase 1" : "Phase 2"}</p>
          <p className="text-[12.5px] uppercase tracking-[0.18em] text-[#8FA79B] font-semibold">STUDIO VISUALIZATION</p>
          <p className="text-[11px] text-white/45 mt-1 mb-6">Rendering &amp; VR production</p>

          <p
            className="text-[34px] leading-none text-white tabular-nums"
            style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
            data-testid="ptl-viz-weeks"
          >
            {vizWeeksDisplay}
          </p>
          <p className="text-[12px] text-white/45 mt-2 tabular-nums" data-testid="ptl-viz-days">
            {vizBusinessDays} business days · includes setup buffer
          </p>

          <p className="text-[10.5px] text-white/40 italic mt-6 pt-4 border-t border-dashed border-white/10">
            {isAlacarte ? "Begins once we receive all required files" : "Runs parallel to Phase 1 · Begins once we receive all required files"}
          </p>
        </div>
      </div>
    </section>
  );
}
